import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useCartStore } from '../store/cart.store';
import { useAuth } from '../hooks/useAuth';
import { addressService } from '../services/address.service';
import { orderService } from '../services/order.service';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/input';
import { Loader2, Check, ChevronRight, AlertCircle } from 'lucide-react';
import { useToast } from '../components/ui/ToastProvider';

const steps = ['Address', 'Shipping', 'Payment', 'Confirm'];

export const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  useAuth();
  const { showToast } = useToast();
  const { items, getSubtotal } = useCartStore();

  const [currentStep, setCurrentStep] = useState(0);
  const [selectedAddressId, setSelectedAddressId] = useState<number | undefined>();
  const [selectedShipping, setSelectedShipping] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Redirect if cart empty
  useEffect(() => {
    if (items.length === 0) {
      navigate('/cart');
    }
  }, [items, navigate]);

  // Fetch addresses
  const { data: addresses, isLoading: addressesLoading } = useQuery({
    queryKey: ['addresses'],
    queryFn: addressService.getAll,
  });

  // Fetch shipping methods
  const { data: shippingMethods, isLoading: shippingLoading } = useQuery({
    queryKey: ['shipping-methods'],
    queryFn: orderService.getShippingMethods,
  });

  useEffect(() => {
    if (shippingMethods && shippingMethods.length > 0 && !selectedShipping) {
      setSelectedShipping(shippingMethods[0].name);
    }
  }, [shippingMethods, selectedShipping]);

  // Extract the API-provided error message so the user gets the actual reason
  // (e.g. "Insufficient stock for X") instead of a generic failure toast.
  const getErrorMessage = (error: unknown, fallback: string): string => {
    const err = error as { response?: { data?: { error?: string } }; message?: string; code?: string };
    if (err?.response?.data?.error) return err.response.data.error;
    if (err?.message) return err.message;
    if (err?.code) return err.code;
    return fallback;
  };

  // Create order mutation
  const createOrderMutation = useMutation({
    mutationFn: orderService.createOrder,
    onSuccess: (order) => {
      // Proceed to payment
      initializePaymentMutation.mutate(order.id);
    },
    onError: (error) => {
      console.error('[checkout] create order failed:', error);
      showToast(getErrorMessage(error, 'Failed to create order'), 'error');
      setIsProcessing(false);
    },
  });

  // Initialize payment mutation
  const initializePaymentMutation = useMutation({
    mutationFn: orderService.initializePayment,
    onSuccess: (data) => {
      // Redirect to payment gateway or handle response
      if (data.paymentUrl) {
        window.location.href = data.paymentUrl;
      } else {
        // Handle inline payment
        navigate(`/checkout/pay/${data.orderId}?reference=${data.reference}`);
      }
    },
    onError: (error) => {
      console.error('[checkout] payment initialization failed:', error);
      showToast(getErrorMessage(error, 'Payment initialization failed'), 'error');
      setIsProcessing(false);
    },
  });

  const subtotal = getSubtotal();
  const tax = Math.round(subtotal * 0.05); // 5% tax
  const selectedShippingMethod = shippingMethods?.find((s) => s.name === selectedShipping) ?? shippingMethods?.[0];
  const shippingCost = selectedShippingMethod?.cost || 0;
  const total = subtotal + tax + shippingCost;

  const handleNext = () => {
    if (currentStep === 0 && !selectedAddressId) {
      showToast('Please select a delivery address', 'error');
      return;
    }
    if (currentStep === 1 && !selectedShipping) {
      showToast('Please select a shipping method', 'error');
      return;
    }
    if (currentStep === 2) {
      handlePlaceOrder();
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddressId || !selectedShipping) {
      showToast('Please complete all steps', 'error');
      return;
    }

    // Prevent double-submitting while a request is already in flight
    if (isProcessing || createOrderMutation.isPending) return;

    setIsProcessing(true);

    // Prepare order data
    const orderData = {
      shippingAddressId: selectedAddressId,
      shippingMethod: selectedShipping,
      notes,
      items: items.map((item) => ({
        productId: item.id,
        quantity: item.quantity,
        variant: item.variant,
      })),
      subtotal: subtotal,
      shippingCost: shippingCost,
      tax: tax,
      discount: 0,
      total: total,
    };

    createOrderMutation.mutate(orderData);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Delivery Address</h3>
            {addressesLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : addresses && addresses.length > 0 ? (
              <div className="space-y-3">
                {addresses.map((address) => (
                  <label
                    key={address.id}
                    className={`flex items-start space-x-3 rounded-lg border p-4 cursor-pointer transition-all ${
                      selectedAddressId === address.id
                        ? 'border-primary bg-primary/5'
                        : 'border-[rgb(var(--border))] hover:border-primary/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="address"
                      value={address.id}
                      checked={selectedAddressId === address.id}
                      onChange={() => setSelectedAddressId(address.id)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{address.label}</span>
                        {address.isDefault && (
                          <span className="text-xs text-primary">(Default)</span>
                        )}
                      </div>
                      <p className="text-sm">{address.name}</p>
                      <p className="text-sm text-[rgb(var(--light-secondary))]">
                        {address.street}, {address.city}, {address.region}
                        {address.postalCode && `, ${address.postalCode}`}
                      </p>
                      <p className="text-sm text-[rgb(var(--light-secondary))]">{address.phone}</p>
                    </div>
                    {selectedAddressId === address.id && (
                      <Check className="h-5 w-5 text-primary" />
                    )}
                  </label>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-[rgb(var(--light-secondary))]">No saved addresses</p>
                <Button variant="secondary" className="mt-2" onClick={() => navigate('/dashboard/profile')}>
                  Add Address
                </Button>
              </div>
            )}
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Shipping Method</h3>
            {shippingLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-3">
                {shippingMethods?.map((method) => (
                  <label
                    key={method.id}
                    className={`flex items-start space-x-3 rounded-lg border p-4 cursor-pointer transition-all ${
                      selectedShipping === method.name
                        ? 'border-primary bg-primary/5'
                        : 'border-[rgb(var(--border))] hover:border-primary/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="shipping"
                      value={method.name}
                      checked={selectedShipping === method.name}
                      onChange={() => setSelectedShipping(method.name)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <span className="font-medium">{method.name}</span>
                        <span className="font-semibold">
                          {method.cost === 0 ? 'Free' : `GHS ${(method.cost / 100).toFixed(2)}`}
                        </span>
                      </div>
                      {method.description && (
                        <p className="text-sm text-[rgb(var(--light-secondary))]">{method.description}</p>
                      )}
                      {method.estimatedDays && (
                        <p className="text-xs text-[rgb(var(--light-secondary))]">
                          Est. {method.estimatedDays}
                        </p>
                      )}
                    </div>
                    {selectedShipping === method.name && (
                      <Check className="h-5 w-5 text-primary" />
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Order Summary & Payment</h3>

            {/* Order Items */}
            <div className="rounded-lg border border-[rgb(var(--border))] p-4 space-y-3">
              {items.map((item) => (
                <div key={item.id} className="flex justify-between text-sm">
                  <span>
                    {item.name} × {item.quantity}
                  </span>
                  <span>GHS {((item.price * item.quantity) / 100).toFixed(2)}</span>
                </div>
              ))}
            </div>

            {/* Cost Breakdown */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[rgb(var(--light-secondary))]">Subtotal</span>
                <span>GHS {(subtotal / 100).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[rgb(var(--light-secondary))]">Shipping</span>
                <span>{shippingCost === 0 ? 'Free' : `GHS ${(shippingCost / 100).toFixed(2)}`}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[rgb(var(--light-secondary))]">Tax (5%)</span>
                <span>GHS {(tax / 100).toFixed(2)}</span>
              </div>
              <div className="border-t border-[rgb(var(--border))] pt-2">
                <div className="flex justify-between font-bold">
                  <span>Total</span>
                  <span className="text-primary text-lg">GHS {(total / 100).toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-primary/5 border border-primary/20 p-4">
              <div className="flex items-start space-x-2">
                <AlertCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[rgb(var(--light-secondary))]">
                  You'll be redirected to the payment gateway to complete your purchase securely.
                </p>
              </div>
            </div>

            <Input
              label="Order Notes (Optional)"
              placeholder="Any special instructions for delivery"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        );

      case 3:
        return (
          <div className="text-center py-8 space-y-4">
            <div className="mx-auto h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
            <h3 className="text-xl font-semibold">Processing Your Order</h3>
            <p className="text-[rgb(var(--light-secondary))]">
              Please wait while we confirm your payment...
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <h1 className="text-3xl font-bold text-[rgb(var(--foreground))]">Checkout</h1>

      {/* Progress Steps */}
      <div className="mt-6 mb-8">
        <div className="flex items-center justify-between">
          {steps.map((_label, index) => (
            <div key={index} className="flex items-center">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                  index <= currentStep
                    ? 'bg-primary text-white'
                    : 'bg-[rgb(var(--border))] text-[rgb(var(--light-secondary))]'
                }`}
              >
                {index + 1}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`h-0.5 w-8 sm:w-16 ${
                    index < currentStep ? 'bg-primary' : 'bg-[rgb(var(--border))]'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="mt-2 flex justify-between text-xs text-[rgb(var(--light-secondary))]">
          {steps.map((label, index) => (
            <span key={index}>{label}</span>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        {renderStep()}

        {currentStep < 3 && (
          <div className="flex justify-between border-t border-[rgb(var(--border))] pt-6">
            <Button
              type="button"
              variant="secondary"
              onClick={handleBack}
              disabled={currentStep === 0}
            >
              Back
            </Button>
            <Button type="button" variant="primary" onClick={handleNext}>
              {currentStep === 2 ? (
                isProcessing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Place Order'
                )
              ) : (
                <>
                  Next
                  <ChevronRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};